import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '@/config/axios';
import { SETTINGS } from '@/config/apiPaths';

// Email settings interface that matches the backend API structure
export interface EmailSettings {
  // Core connection settings
  server: string;
  port: number;
  username: string;
  password: string;
  
  // IMAP specific settings
  imapHost: string;
  imapPort: number;
  imapEncryption: 'tls' | 'ssl' | 'none';
  imapMailbox: string;
  imapMarkSeen: boolean;
  imapFetchUnreadOnly: boolean;
  
  // Legacy/compatibility
  protocol: 'imap';
  mailbox: string;
  use_ssl: boolean;
  validate_cert: boolean;
}

// Local-only (persisted) Template Settings for document generation and placeholder resolution
export interface TemplateComponentConfig {
  // Unique key for the template, e.g. 'default_contract_pdf'
  key: string;
  // Human-friendly label to show in UI
  label: string;
  // Can be toggled without deleting
  enabled?: boolean;
  // Optional per-template document subtitle override
  document_subtitle?: string;
  // Arbitrary template-specific settings (key/value)
  settings?: { key: string; value: string }[];
}

export interface TemplateSettings {
  // Currently selected template key for contracts
  activeTemplateKey: string;
  // Available templates
  templates: TemplateComponentConfig[];
}

export interface CompanySettings {
  id?: number;
  name: string;
  logo?: string;
  address: {
    street: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  contact: {
    email: string;
    phone: string;
    website: string;
  };
  tax_info: {
    tax_id: string;
    vat_number?: string;
  };
  terms_and_conditions?: string;
  // Document settings
  document_subtitle?: string;
  created_at?: string;
  updated_at?: string;
}

interface SettingsState {
  // UI state
  isLoading: boolean;
  error: string | null;
  
  // Local settings
  companyName: string;
  emailSettings: EmailSettings;
  companySettings: CompanySettings | null;
  templateSettings: TemplateSettings;
  
  // Local settings methods
  setCompanySettings: (settings: CompanySettings) => void;
  setEmailSettings: (settings: Partial<EmailSettings>) => void;
  resetEmailSettings: () => void;
  clearError: () => void;
  setTemplateSettings: (settings: TemplateSettings) => void;
  updateTemplateSettings: (settings: Partial<TemplateSettings>) => void;
  
  // API methods for email settings
  fetchEmailSettings: () => Promise<EmailSettings>;
  updateEmailSettings: (settings: Partial<EmailSettings>) => Promise<EmailSettings>;
  testEmailConnection: (settings: Partial<EmailSettings>) => Promise<{ success: boolean; message: string }>;
  
  // API methods for company settings
  fetchCompanySettings: () => Promise<CompanySettings>;
  updateCompanySettings: (settings: Partial<CompanySettings>) => Promise<CompanySettings>;
}

const defaultEmailSettings: EmailSettings = {
  // Core connection settings
  server: 'imap.gmail.com',
  port: 993,
  username: '',
  password: '',
  
  // IMAP specific settings (same as core for consistency)
  imapHost: 'imap.gmail.com',
  imapPort: 993,
  imapEncryption: 'ssl',
  imapMailbox: 'INBOX',
  imapMarkSeen: false,
  imapFetchUnreadOnly: true,
  
  // Legacy/compatibility
  protocol: 'imap',
  mailbox: 'INBOX',
  use_ssl: true,
  validate_cert: true,
};


// No conversion needed - we're using the API format directly

const defaultTemplateSettings: TemplateSettings = {
  activeTemplateKey: 'default_contract_pdf',
  templates: [
    {
      key: 'default_contract_pdf',
      label: 'Default Contract PDF',
      enabled: true,
      document_subtitle: 'Federal Republic of Nigeria',
      settings: [
        { key: 'document_subtitle', value: 'Federal Republic of Nigeria' },
      ],
    },
  ],
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // UI state
      isLoading: false,
      error: null,
      
      // Local settings
      // Real value is loaded via setCompanySettings once org settings fetch;
      // this default is the fallback shown before that resolves (or if it
      // never does), so it must not be a raw template placeholder — the
      // footer showed "Your Company Name" to every visitor, including the
      // public demo login, since nothing was ever wired up to replace it.
      companyName: 'Re:Loam',
      emailSettings: { ...defaultEmailSettings },
      companySettings: null,
      templateSettings: { ...defaultTemplateSettings },
      
      // Local settings methods
      setCompanySettings: (settings: CompanySettings) => set({ companyName: settings.name, ...settings }),
      setEmailSettings: (settings: Partial<EmailSettings>) => 
        set((state) => ({
          emailSettings: { ...state.emailSettings, ...settings }
        })),
      resetEmailSettings: () => 
        set({ emailSettings: { ...defaultEmailSettings } }),
      clearError: () => set({ error: null }),
      setTemplateSettings: (settings: TemplateSettings) => set({ templateSettings: settings }),
      updateTemplateSettings: (settings: Partial<TemplateSettings>) =>
        set((state) => ({
          templateSettings: { ...state.templateSettings, ...settings,
            templates: settings.templates ?? state.templateSettings.templates,
          },
        })),
      
      // API methods for email settings
      fetchEmailSettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get(SETTINGS.EMAIL);
          const settings = response.data;
          
          // Normalize possible backend keys (Host, Port, etc.) into our EmailSettings shape
          const normalized: EmailSettings = {
            // Core connection settings
            server: settings.Host ?? settings.server ?? '',
            port: settings.Port ?? settings.port ?? 993,
            username: settings.Username ?? settings.username ?? '',
            password: settings.Password ?? settings.password ?? '',
            
            // IMAP specific (kept in sync with core)
            imapHost: (settings.Host ?? settings.server ?? ''),
            imapPort: (settings.Port ?? settings.port ?? 993),
            imapEncryption: (settings.UseSSL ?? settings.use_ssl ?? true) ? 'ssl' : 'none',
            imapMailbox: settings.Mailbox ?? settings.mailbox ?? 'INBOX',
            imapMarkSeen: settings.imapMarkSeen ?? false,
            imapFetchUnreadOnly: settings.imapFetchUnreadOnly ?? true,
            
            // Legacy/compat
            protocol: settings.Protocol ?? settings.protocol ?? 'imap',
            mailbox: settings.Mailbox ?? settings.mailbox ?? 'INBOX',
            use_ssl: settings.UseSSL ?? settings.use_ssl ?? true,
            validate_cert: settings.ValidateCert ?? settings.validate_cert ?? true,
          };
          
          // Update local settings
          set({ 
            emailSettings: normalized,
            isLoading: false 
          });
          
          return normalized;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch email settings';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },
      
      updateEmailSettings: async (settings: Partial<EmailSettings>) => {
        set({ isLoading: true, error: null });
        try {
          // Build backend payload without IMAP-prefixed keys
          const payload = {
            Host: settings.imapHost ?? settings.server ?? '',
            Port: settings.imapPort ?? settings.port ?? 993,
            Username: settings.username ?? '',
            Password: settings.password ?? '',
            Mailbox: settings.mailbox ?? settings.imapMailbox ?? 'INBOX',
            UseSSL: (settings.use_ssl ?? ((settings.imapEncryption === 'ssl' || settings.imapEncryption === 'tls') ? true : false)) ?? true,
            ValidateCert: settings.validate_cert ?? true,
            Protocol: settings.protocol ?? 'imap',
          };
          const response = await api.put(SETTINGS.EMAIL, payload);
          const responseData = response.data;
          
          // Check if the response contains a full settings object or just a success message
          // If it's just a message with partial data, merge with existing settings
          const currentSettings = get().emailSettings;
          const normalizedResponse: Partial<EmailSettings> = responseData && !responseData.message ? {
            server: responseData.Host ?? responseData.server,
            port: responseData.Port ?? responseData.port,
            username: responseData.Username ?? responseData.username,
            password: responseData.Password ?? responseData.password,
            imapHost: (responseData.Host ?? responseData.server),
            imapPort: (responseData.Port ?? responseData.port),
            imapEncryption: (responseData.UseSSL ?? responseData.use_ssl) ? 'ssl' : 'none',
            imapMailbox: responseData.Mailbox ?? responseData.mailbox,
            imapMarkSeen: responseData.imapMarkSeen,
            imapFetchUnreadOnly: responseData.imapFetchUnreadOnly,
            protocol: responseData.Protocol ?? responseData.protocol,
            mailbox: responseData.Mailbox ?? responseData.mailbox,
            use_ssl: responseData.UseSSL ?? responseData.use_ssl,
            validate_cert: responseData.ValidateCert ?? responseData.validate_cert,
          } : {};
          const updatedSettings: EmailSettings = responseData.message
            ? { ...currentSettings, ...payload, ...normalizedResponse } as EmailSettings
            : {
                // Fall back to the payload if API echoes nothing useful
                server: normalizedResponse.server ?? payload.Host,
                port: normalizedResponse.port ?? payload.Port,
                username: normalizedResponse.username ?? payload.Username,
                password: normalizedResponse.password ?? payload.Password,
                imapHost: normalizedResponse.imapHost ?? payload.Host,
                imapPort: normalizedResponse.imapPort ?? payload.Port,
                imapEncryption: normalizedResponse.imapEncryption ?? ((payload.UseSSL ? 'ssl' : 'none') as 'ssl' | 'tls' | 'none'),
                imapMailbox: normalizedResponse.imapMailbox ?? payload.Mailbox,
                imapMarkSeen: normalizedResponse.imapMarkSeen ?? (currentSettings.imapMarkSeen ?? false),
                imapFetchUnreadOnly: normalizedResponse.imapFetchUnreadOnly ?? (currentSettings.imapFetchUnreadOnly ?? true),
                protocol: normalizedResponse.protocol ?? payload.Protocol,
                mailbox: normalizedResponse.mailbox ?? payload.Mailbox,
                use_ssl: normalizedResponse.use_ssl ?? payload.UseSSL,
                validate_cert: normalizedResponse.validate_cert ?? payload.ValidateCert,
              };
          
          console.log('API response:', responseData);
          console.log('Updated settings to save:', updatedSettings);
          
          // Update local settings
          set({ 
            emailSettings: updatedSettings,
            isLoading: false 
          });
          
          return updatedSettings;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to update email settings';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },
      
      testEmailConnection: async (settings: Partial<EmailSettings>) => {
        set({ isLoading: true, error: null });
        try {
          const s = settings || get().emailSettings;
          const payload = {
            Host: s.imapHost ?? s.server ?? '',
            Port: s.imapPort ?? s.port ?? 993,
            Username: s.username ?? '',
            Password: s.password ?? '',
            Mailbox: s.mailbox ?? s.imapMailbox ?? 'INBOX',
            UseSSL: (s.use_ssl ?? ((s.imapEncryption === 'ssl' || s.imapEncryption === 'tls') ? true : false)) ?? true,
            ValidateCert: s.validate_cert ?? true,
            Protocol: s.protocol ?? 'imap',
          };
          const response = await api.post(SETTINGS.EMAIL_TEST_CONNECTION, payload, {
            timeout: 30000 // 30 second timeout for email connections
          });
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to test email connection';
          set({ error: errorMessage, isLoading: false });
          return { success: false, message: errorMessage };
        }
      },
      
      // API methods for company settings
      fetchCompanySettings: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get(SETTINGS.COMPANY);
          const companySettings = response.data;

          console.log('Fetched company settings:', companySettings);
          
          set({ 
            companySettings,
            companyName: companySettings.name, // Also update the simplified name
            isLoading: false 
          });
          
          return companySettings;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch company settings';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },
      
      updateCompanySettings: async (settings: Partial<CompanySettings>) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.put(SETTINGS.COMPANY, settings);
          const companySettings = response.data;

          set({
            companySettings,
            companyName: settings.name || get().companyName, // Update the simplified name if provided
            isLoading: false
          });

          return companySettings;
        } catch (error: any) {
          console.error('API Error Details:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
          });
          const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update company settings';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },
    }),
    {
      name: 'settings-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist local settings, not API state or functions
        companyName: state.companyName,
        emailSettings: state.emailSettings,
        companySettings: state.companySettings,
        templateSettings: state.templateSettings,
      }),
    }
  )
);
